import React, { forwardRef, useEffect } from "react";

// For table checkboxes
// see https://react-table.tanstack.com/docs/examples/row-selection

const isMutableRef = (
  ref: React.Ref<HTMLInputElement> | React.MutableRefObject<null>
): ref is React.MutableRefObject<null> => {
  return typeof ref !== "function";
};

interface IIndeterminateInputProps {
  indeterminate?: boolean;
  disabled?: boolean;
}

const useCombinedRefs = (
  ...refs: Array<React.Ref<HTMLInputElement> | React.MutableRefObject<null>>
): React.MutableRefObject<HTMLInputElement | null> => {
  const targetRef = React.useRef(null);

  useEffect(() => {
    refs.forEach(
      (ref: React.Ref<HTMLInputElement> | React.MutableRefObject<null>) => {
        if (!ref) return;

        if (isMutableRef(ref)) {
          ref.current = targetRef.current;
        } else if (typeof ref === "function") {
          ref(targetRef.current);
        }
      }
    );
  }, [refs]);

  return targetRef;
};

export const IndeterminateCheckbox = forwardRef<
  HTMLInputElement,
  IIndeterminateInputProps
>(({ indeterminate, disabled, ...rest }, ref: React.Ref<HTMLInputElement>) => {
  const defaultRef = React.useRef(null);
  const combinedRef = useCombinedRefs(ref, defaultRef);

  useEffect(() => {
    if (combinedRef?.current) {
      combinedRef.current.indeterminate = indeterminate ?? false;
    }
  }, [combinedRef, indeterminate]);

  return (
    <label className="bp3-control bp3-checkbox">
      <input
        type="checkbox"
        ref={combinedRef}
        {...rest}
        disabled={disabled}
        data-testid="table-checkbox"
      />
      <span className="bp3-control-indicator"></span>
    </label>
  );
});
